import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { SalesLineChart } from "@/components/charts/SalesLineChart";
import { PendingStatus } from "@/components/ui/pending-indicator";
import { cn } from "@/lib/utils";
import {
  fetchDeskSalesSeries,
  type DeskGlanceSalesToday,
} from "@/shared/api/desk";
import { formatGlanceMoney, vsYesterdayLabel } from "./owner-glance-math";
import { HomeMetricCard } from "./HomeMetricCard";
import {
  HomePeriodCustomForm,
  HomePeriodPresetToggle,
} from "./HomePeriodControls";
import {
  homePeriodLabel,
  loadHomePeriod,
  saveHomePeriod,
  type HomePeriodState,
} from "./home-metric-period";

const ZERO_WEEK = ["0", "0", "0", "0", "0", "0", "0"] as const;
const ZERO_MONTH = Array.from({ length: 30 }, () => "0");

function todayYmdLocal(): string {
  const n = new Date();
  const y = n.getFullYear();
  const m = String(n.getMonth() + 1).padStart(2, "0");
  const d = String(n.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function monthStartYmd(ymd: string): string {
  return `${ymd.slice(0, 7)}-01`;
}

/** Sales trend with 7d · 30d · MTD · Custom — plan/50. */
export function SalesTrendWidget(props: {
  companySlug: string;
  currencyCode: string;
  salesToday: DeskGlanceSalesToday | null | undefined;
  loading?: boolean;
  onCustomize?: () => void;
  className?: string;
}) {
  const { companySlug, currencyCode, salesToday, loading, className } = props;
  const [period, setPeriod] = useState<HomePeriodState>(() =>
    loadHomePeriod("salesTrend", companySlug),
  );
  const [showMoreOpen, setShowMoreOpen] = useState(false);
  const [draftFrom, setDraftFrom] = useState(
    () => period.from ?? monthStartYmd(todayYmdLocal()),
  );
  const [draftTo, setDraftTo] = useState(() => period.to ?? todayYmdLocal());

  const customQuery = useQuery({
    queryKey: [
      "desk",
      "sales-series",
      companySlug,
      period.from,
      period.to,
    ],
    queryFn: ({ signal }) =>
      fetchDeskSalesSeries(companySlug, period.from!, period.to!, signal),
    enabled:
      period.preset === "custom" && Boolean(period.from && period.to),
  });

  let series: string[];
  if (period.preset === "custom") {
    series = customQuery.data?.series ?? [];
  } else if (period.preset === "30") {
    series =
      salesToday?.last30?.length === 30
        ? salesToday.last30
        : [...ZERO_MONTH];
  } else if (period.preset === "mtd") {
    series =
      salesToday?.lastMtd && salesToday.lastMtd.length > 0
        ? salesToday.lastMtd
        : ["0"];
  } else {
    series =
      salesToday?.last7?.length === 7 ? salesToday.last7 : [...ZERO_WEEK];
  }

  const amount = salesToday?.amount ?? "0";
  const yesterdayAmount = salesToday?.yesterdayAmount ?? "0";
  const today =
    formatGlanceMoney(amount, currencyCode, {
      maxFractionDigits: 0,
      zeroOk: true,
    }) ?? amount;
  const vs = salesToday
    ? vsYesterdayLabel(amount, yesterdayAmount, currencyCode)
    : loading
      ? "Loading…"
      : "vs yesterday · —";
  const description = homePeriodLabel(period, "sales");

  const persist = (next: HomePeriodState) => {
    setPeriod(next);
    saveHomePeriod("salesTrend", companySlug, next);
  };

  const bodyLoading =
    (loading && !salesToday && period.preset !== "custom") ||
    (period.preset === "custom" &&
      customQuery.isPending &&
      !customQuery.data);

  const headline = (
    <div className="min-w-0">
      <div className="font-[family-name:var(--font-display)] text-2xl font-semibold tabular-nums tracking-tight text-[var(--color-foreground)]">
        {today}
      </div>
      <p className="mt-1 m-0 text-xs text-[var(--color-muted)]">{vs}</p>
    </div>
  );

  const actions = (
    <div className="flex flex-wrap items-center gap-2">
      <HomePeriodPresetToggle
        state={period}
        onPreset={(p) => persist({ preset: p })}
        onCustom={() => {
          setDraftFrom(period.from ?? monthStartYmd(todayYmdLocal()));
          setDraftTo(period.to ?? todayYmdLocal());
          setShowMoreOpen(true);
        }}
      />
      <Link
        to={`/c/${companySlug}/pos/tickets`}
        className="shrink-0 text-xs font-medium text-[var(--color-primary)] no-underline hover:underline"
      >
        POS sales
      </Link>
    </div>
  );

  const chartBlock = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      {headline}
      <div className="w-full max-w-[14rem] sm:w-56">
        <SalesLineChart values={series} currencyCode={currencyCode} compact />
      </div>
    </div>
  );

  return (
    <HomeMetricCard
      title="Sales trend"
      description={description}
      size="wide"
      className={cn(className)}
      onCustomize={props.onCustomize}
      actions={actions}
      showMoreOpen={showMoreOpen}
      onShowMoreOpenChange={setShowMoreOpen}
      expandedExtra={
        <HomePeriodCustomForm
          idPrefix="sales-trend-period"
          from={draftFrom}
          to={draftTo}
          onFrom={setDraftFrom}
          onTo={setDraftTo}
          onApply={() =>
            persist({ preset: "custom", from: draftFrom, to: draftTo })
          }
          pending={customQuery.isFetching && period.preset === "custom"}
          error={
            customQuery.isError ? "Could not load this range." : null
          }
        />
      }
      compact={
        bodyLoading ? (
          <PendingStatus>Loading sales…</PendingStatus>
        ) : (
          chartBlock
        )
      }
      expanded={
        bodyLoading ? (
          <PendingStatus>Loading sales…</PendingStatus>
        ) : (
          <div className="flex flex-col gap-4">
            {headline}
            <SalesLineChart values={series} currencyCode={currencyCode} />
          </div>
        )
      }
    />
  );
}
